import {DialogTitle} from '@headlessui/react';
import {ExclamationCircleIcon} from '@heroicons/react/20/solid';
import React, {ComponentType, ReactNode} from 'react';

import classNames from 'classnames';
import {Button} from '../../Button';
import {ModalDialog} from './ModalDialog';

interface IProps {
  open: boolean;
  maxWidth?: React.ComponentProps<typeof ModalDialog>['maxWidth'];
  disableClose?: boolean;
  Icon?: ComponentType;
  title: string;
  children: ReactNode;
  disableAction?: boolean;
  actionButtonLabel?: string;
  buttonCategory?:
    | 'primary'
    | 'secondary'
    | 'success'
    | 'secondarycolor'
    | 'danger'
    | undefined;
  onAction?: () => void;
  onClose: () => void;
}

export const AlertDialog: React.FC<IProps> = (props) => {
  const {
    buttonCategory = 'primary',
    actionButtonLabel = 'OK',
    Icon = ExclamationCircleIcon,
    maxWidth = 'md',
  } = props;

  const iconBackground = {
    primary: 'bg-blue-100',
    secondary: 'bg-gray-100',
    success: 'bg-green-100',
    secondarycolor: 'bg-gray-100',
    danger: 'bg-red-100',
  };
  const iconColors = {
    primary: 'text-blue-600',
    secondary: 'text-gray-600',
    success: 'text-green-600',
    secondarycolor: 'text-gray-600',
    danger: 'text-red-600',
  };

  const _onClose = () => {
    if (props.disableClose) {
      return;
    }
    props.onClose();
  };

  return (
    <ModalDialog open={props.open} maxWidth={maxWidth} onClose={_onClose}>
      <div className="sm:flex sm:items-start overflow-x-hidden">
        <div
          className={classNames(
            'mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full sm:mx-0 sm:h-10 sm:w-10',
            iconBackground[buttonCategory] ?? 'text-blue-100',
          )}>
          <Icon
            className={classNames(
              'h-6 w-6',
              iconColors[buttonCategory] ?? 'text-blue-600',
            )}
            aria-hidden="true"
          />
        </div>
        <div className="w-full mt-3 text-center sm:ml-4 sm:mr-2 sm:mt-0 sm:text-left">
          <DialogTitle
            as="h3"
            className="text-base font-semibold leading-6 text-gray-900">
            {props.title}
          </DialogTitle>
          <div className="mt-4">
            <div className="text-sm text-gray-500">{props.children}</div>
          </div>
        </div>
      </div>
      <div className="mt-6 sm:ml-10 sm:mt-6 flex sm:pl-4 align-middle gap-4">
        {props.onAction && (
          <div>
            <Button
              label={actionButtonLabel}
              category={buttonCategory}
              disabled={props.disableAction}
              onClick={props.onAction}
              buttonProps={{
                type: 'button',
              }}
            />
          </div>
        )}
        <div>
          <Button
            label="Close"
            category="secondary"
            disabled={props.disableClose}
            onClick={props.onClose}
            buttonProps={{
              type: 'button',
            }}
          />
        </div>
      </div>
    </ModalDialog>
  );
};
